import {useMemo} from 'react';
import PropTypes from 'prop-types';

import {Box, Text, useMultiStyleConfig} from '@amblique/base/app/components/shared/ui';
import {Cf013CurtainView} from '@amblique/king-living/app/components/cf013-curtain-view';
import {ShopTheLookEmbedProvider} from '@amblique/king-living/app/contentful/models/shop-the-look-embed-context';
import {resolveHotspotContentImageUrl} from '@amblique/king-living/app/utils/shop-the-look-hotspot-content';

/** Full-bleed section shell: same `Swiper` `CF-013` root as Media Carousel. */
const SWIPER_ROOT_VARIANT = 'CF-013';

/** Copy / eyebrow / headline in `contentful/carousel-item` (shared `CF-013` tokens). */
const CAROUSEL_ITEM_VARIANT = 'CF-013';

/**
 * Curated main image slides + optional Contentful thumb entries, index-aligned (same as media carousel).
 *
 * @param {{ items?: unknown[] } | null} mainImagesCollection
 * @param {{ items?: unknown[] } | null} thumbnailImagesCollection
 * @returns {{ mainItems: { id: string, src: string, alt: string }[], thumbImageEntries: object[] }}
 */
function mapMainSlidesAndThumbs(mainImagesCollection, thumbnailImagesCollection) {
  const mainList = mainImagesCollection?.items;

  if (!Array.isArray(mainList)) {
    return {mainItems: [], thumbImageEntries: []};
  }

  const fromThumbs = (thumbnailImagesCollection?.items ?? []).filter((i) => i && i.sys?.id);
  const useThumbs = fromThumbs.length > 0;
  const mainItems = [];
  const thumbImageEntries = [];
  let k = 0;

  for (const item of mainList) {
    if (item?.sys?.id) {
      const src = resolveHotspotContentImageUrl(item);

      if (src) {
        mainItems.push({
          id: item.sys.id,
          src,
          alt: item.altText || item.title || item.description || '',
        });
        if (useThumbs) {
          thumbImageEntries.push(fromThumbs[k] ?? item);
        } else {
          thumbImageEntries.push(item);
        }
        k += 1;
      }
    }
  }

  return {mainItems, thumbImageEntries};
}

/**
 * King Difference — Media (CF-013). Curtain transition between main images, thumbnails, swipe, autoplay.
 * Styling: `Swiper` `CF-013` (root) + `CF-013-Thumbs` (thumb strip); `contentful/carousel-item` `CF-013` for text 1–3.
 */
export const KingDifferenceMediaCf013 = ({component, isComplete = true, isFetching = false, className, ...props}) => {
  if (!component?.sys?.id) {
    return null;
  }

  const rootClassName = [className, 'king-difference-media-cf013'].filter(Boolean).join(' ');

  const {autoplay, mainImagesCollection, text1, text2, text3, thumbnailImagesCollection} = component;

  const textStyles = useMultiStyleConfig('contentful/carousel-item', {
    component,
    isComplete,
    isFetching,
    variant: CAROUSEL_ITEM_VARIANT,
  });

  const stylesMain = useMultiStyleConfig('Swiper', {variant: SWIPER_ROOT_VARIANT});

  const {mainItems, thumbImageEntries} = useMemo(
    () => mapMainSlidesAndThumbs(mainImagesCollection, thumbnailImagesCollection),
    [mainImagesCollection, thumbnailImagesCollection]
  );

  const hasText = text1 || text2 || text3;
  const hasSlides = mainItems.length > 0;

  if (isComplete && !hasText && !hasSlides) {
    return null;
  }

  return isComplete ? (
    <Box data-component="king-difference-media-cf013" w="100%" {...props} className={rootClassName}>
      {hasText && (
        <Box {...textStyles?.root} className="king-difference-media-cf013__copy">
          <Box {...textStyles?.group1} className="king-difference-media-cf013__copy-inner">
            {text1 && (
              <Text as="p" {...textStyles.text1} className="king-difference-media-cf013__eyebrow">
                {text1}
              </Text>
            )}
            {text2 && (
              <Text as="h2" {...textStyles.text2} className="king-difference-media-cf013__heading">
                {text2}
              </Text>
            )}
            {text3 && (
              <Text as="p" {...textStyles.text3} className="king-difference-media-cf013__description">
                {text3}
              </Text>
            )}
          </Box>
        </Box>
      )}

      {hasSlides && (
        <ShopTheLookEmbedProvider>
          <Cf013CurtainView
            autoPlay={autoplay === true}
            mainItems={mainItems}
            stylesRoot={stylesMain}
            thumbImageEntries={thumbImageEntries}
          />
        </ShopTheLookEmbedProvider>
      )}
    </Box>
  ) : null;
};

KingDifferenceMediaCf013.displayName = 'KingDifferenceMediaCf013';
KingDifferenceMediaCf013.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
